import type { ComparisonValue } from '@epilot/journey-logic-commons/src/types/next'
import { LOGIC_OPERATOR } from '@epilot/journey-logic-commons/src/types/next'

import { Operator } from '../operator'
import type { EnrichedRuntimeContext, FunctionHandler } from '../types'

export class CustomFunction<A = unknown> extends Operator<unknown, A> {
  /** Given funciton name, theoretically unique */
  protected readonly __name: string
  /** Provided handler responsible for running the custom evaluation logic */
  protected handler: FunctionHandler
  /** Timeout before cancelling and self-resolving to false */
  protected readonly timeoutMs: number

  constructor(
    /** An unique name for this function handler */
    name: string,
    /** The actual handler method */
    fn: FunctionHandler,
    /** A maximum allowed timeout for the handler to resolve */
    timeoutMs: number
  ) {
    super(LOGIC_OPERATOR.CUSTOM_FUNCTION)
    this.__name = name
    this.handler = fn
    this.timeoutMs = timeoutMs
  }

  async evaluate(
    factValue: unknown,
    comparisonValue: ComparisonValue,
    context: EnrichedRuntimeContext,
    args?: A
  ): Promise<boolean> {
    let timer: number | undefined

    try {
      const controller = new AbortController()

      // short-circuits the handler if it takes too long and signals abortion
      const abortionResolver = () =>
        new Promise<false>((resolve) => {
          timer = window.setTimeout(() => {
            controller.abort()
            resolve(false)
          }, this.timeoutMs)
        })

      return await Promise.race([
        this.handler(
          factValue,
          comparisonValue,
          context,
          args,
          controller.signal
        ),
        abortionResolver()
      ])
    } catch (originalError) {
      const error = new Error(
        `Failed to evaluate custom function (${this.__name})`
      )

      error.cause = originalError

      // eslint-disable-next-line no-console
      console.error(error)

      return false
    } finally {
      window.clearTimeout(timer) // wont leave it hanging
    }
  }
}
